'use client'

import Box from '@mui/material/Box'
import Link from '@mui/material/Link'
import Step from '@mui/material/Step'
import StepLabel from '@mui/material/StepLabel'
import Stepper from '@mui/material/Stepper'
import { useTranslations } from 'next-intl'
import { useRouter } from 'next/navigation'
import React from 'react'
import toast from '~/app/_components/toast'
import { useAnalytics } from '~/app/_hooks/use_analytics'
import { useChallenge } from '~/app/sign/_hooks/use_challenge'
import client from '~/lib/client/client'
import CodeError from '~/lib/client/error'

import type { AuthUserId } from '~/models/auth_user'

import Step1, { Step1Data } from './step_one'
import Step2, { Step2Data } from './step_two'

interface FormProps {
  partner: {
    id: AuthUserId
    username: string
  } | null
}

const getSteps = (t: ReturnType<typeof useTranslations<'auth.sign_up.form'>>) => [
  t('steps.account'),
  t('steps.personal'),
]

export default function Form({ partner: p }: FormProps) {
  const t = useTranslations('auth.sign_up.form')
  const tLinks = useTranslations('auth.sign_up.links')
  const [loading, setLoading] = React.useState(false)
  const [step, setStep] = React.useState(0)
  const [step1Data, setStep1Data] = React.useState<Step1Data | null>(null)
  const router = useRouter()
  const analytics = useAnalytics()
  const { challenge } = useChallenge()

  const handleStep1Complete = (data: Step1Data) => {
    setStep1Data(data)
    setStep(1)
  }

  const handleBack = () => {
    setStep(0)
  }

  const handleStep2Submit = async (
    data: Step2Data,
  ): Promise<{ field: keyof Step2Data; message: string } | null> => {
    if (!step1Data) return null

    setLoading(true)

    const pow = await challenge()
    const [_, err] = await client({
      path: '/api/auth/register',
      method: 'POST',
      body: {
        email: step1Data.email,
        password: step1Data.password,
        sponsor_username: step1Data.sponsor_username,
        first_name: data.first_name,
        last_name: data.last_name,
        username: data.username,
        dob: data.dob,
        country: data.country,
        ...pow,
      },
    })

    setLoading(false)

    if (err) {
      if (err.details?.constraint === 'auth_user_username_idx') {
        return { field: 'username', message: t('username.error.exists') }
      }

      if (
        err.details?.constraint === 'auth_user_email_idx' ||
        err.code == CodeError.code.AlreadyExists
      ) {
        setStep(0)
        toast({
          type: 'error',
          message: t('errors.email_exists'),
        })
        return null
      }

      toast({
        type: 'error',
        message: t('errors.default'),
      })
      return null
    }

    analytics.track('signup', {
      email: step1Data.email,
      name: `${data.first_name} ${data.last_name}`,
    })

    router.refresh()
    return null
  }

  return (
    <>
      <Stepper
        activeStep={step}
        sx={{
          mb: 4,
          '& .MuiStepLabel-label': {
            color: 'rgba(255, 255, 255, 0.5)',
            '&.Mui-active': { color: '#f0f0f1' },
            '&.Mui-completed': { color: '#59CDA1' },
          },
          '& .MuiStepIcon-root': {
            color: '#2a2a2a',
            '&.Mui-active': { color: '#27B1C6' },
            '&.Mui-completed': { color: '#59CDA1' },
          },
        }}
      >
        {getSteps(t).map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {step === 0 && (
        <Step1
          partner={p}
          onNext={handleStep1Complete}
          defaultValues={step1Data ?? undefined}
        />
      )}

      {step === 1 && (
        <Step2 onBack={handleBack} onSubmit={handleStep2Submit} loading={loading} />
      )}

      <Box
        component='div'
        sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: 1,
          fontSize: '14px',
          mt: 3,
        }}
      >
        <Box component='span' sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
          {tLinks('have_account')}
        </Box>
        <Link href='/sign/in' sx={{ fontWeight: 600 }}>
          {tLinks('login')}
        </Link>
      </Box>

      <Box sx={{ textAlign: 'center', mt: 1 }}>
        <Link href='/sign/email?reason=password_reset' sx={{ fontSize: '14px' }}>
          {tLinks('forgot_password')}
        </Link>
      </Box>
    </>
  )
}
